// Manufacturing projects (ERP-PLAN §5.12), spread into `mutations` (shared/mutations.ts): linking Shopify products to a
// project (their variants join it), part types (one or many at once), and BOM modules (drafts only). A project's
// basics are fields of project.update (projectFields). Rules and implementations: server/src/mutations/projects.ts
// (links, part types) and server/src/mutations/boms.ts (modules, beside the other BOM rules).
import { z } from "zod";
import { custom, STAFF } from "./mutation-kit.ts";
import { PART_TYPES } from "./projects.ts";

const productId = z.string().regex(/^gid:\/\/shopify\/Product\/\d+$/, "Not a Shopify product id");
/** A module name ("Electronics"); '' takes the line out of every module. */
export const sectionName = z.string().trim().max(60, "Keep module names under 60 characters");

export const projectMutations = {
  /**
   * Puts a Shopify product in a project (`id` is the new link's, made by the browser). Its live variants join the project
   * (item.project_id), marked with the link (item.project_link_id) so unlinking takes out only those, and its BOMs get
   * the project. Variants or BOMs in another project move only with `move` (refused otherwise, naming them); a moved
   * variant remembers its project (item.project_link_prev_id) and goes back to it on unlink.
   * Returns { id, variants, moved: [{ sku, from }], boms: [{ id, revision, status, from }] }.
   */
  "project.link_product": custom(
    z.object({
      id: z.uuid(),
      project_id: z.uuid(),
      shopify_product_id: productId,
      position: z.number().int().min(0).max(10_000).optional(),
      move: z.boolean().optional(),
    }),
    STAFF,
  ),
  /**
   * Takes a product out of its project: variants the link put in leave it (back to the project it took them from),
   * ones people put there stay, and its BOMs pointing at the project point at none. Returns { id, cleared, restored, boms }.
   */
  "project.unlink_product": custom(z.object({ id: z.uuid() }), STAFF),
  /** Sets the part type of one or many items (it drives the files their specs should have). Returns { changed }. */
  "item.set_part_type": custom(z.object({ item_ids: z.array(z.uuid()).min(1, "Pick at least one part").max(2000), part_type: z.enum(PART_TYPES) }), STAFF),
  /**
   * A draft BOM's modules: the order they're shown in, and each listed line's module (lines not listed keep theirs).
   * Without `order`, it becomes the modules in line order. Returns { changed }.
   */
  "bom.set_sections": custom(
    z.object({
      id: z.uuid(),
      order: z.array(sectionName.min(1)).max(40).optional(),
      lines: z
        .array(z.object({ id: z.uuid(), section: sectionName }))
        .max(2000)
        .optional(),
    }),
    STAFF,
  ),
} as const;
